import { useEffect, useState } from 'react'

import { Box, Button, Flex } from 'app/components/design-system-components'
import { UndoRounded } from 'app/components/design-system-components/icons/content'
import HeadingAndLabel, {
  STATUS_CONFIRMED,
} from 'app/components/display/smartdocs/headingAndLabel' // STATUS_UNVERIFIED,
import { getTotalAndCheckItemNumber } from 'app/components/display/smartdocs/helpers'
import theme from 'app/match/applicationReportPDF/assets/theme'

const SHOW_MORE = 'Show More'
const SHOW_LESS = 'Show Less'

export interface DataType {
  analysis: Analysis
}

interface ConfirmationStatus {
  confirmed_at: string
  confirmed_by: string
  id: string
}

interface AnalysisItem {
  id: string
  label: string
  status: string
  message: string
  confirmation_details: ConfirmationStatus[] | null
  doc_id: string
  file_name: string
}

interface Analysis {
  [key: string]: {
    fields: AnalysisItem[]
    is_valid: boolean
  }
}

interface FieldsAndValidity {
  fields: AnalysisItem[]
  is_valid: boolean
}

interface ExplainerProps {
  data?: DataType | undefined
  isCopilot?: boolean
  onClickConfirmOKForAnalysis?: (analysisGUID: string) => void
  onClickUndoForAnalysis?: (
    analysisGUID: string,
    confirmationGUID: string,
  ) => void
  isRenterSide?: boolean
  showOpenViewerLink?: boolean
  toggleDocumentsViewer?: ((docGUID: string) => void) | undefined
}

function Explainer({
  data,
  isCopilot = false,
  onClickConfirmOKForAnalysis,
  onClickUndoForAnalysis,
  isRenterSide = true,
  showOpenViewerLink = false,
  toggleDocumentsViewer,
}: ExplainerProps) {
  const [showMoreEnabled, setShowMoreEnabled] = useState(false)
  const [showLink, setShowLink] = useState(true)
  const [displayData, setDisplayData] = useState<FieldsAndValidity[]>([])

  useEffect(() => {
    if (data) {
      setDisplayData(Object.values(data.analysis).flat())
      const { totalDocNumber, docNumberToCheck } = getTotalAndCheckItemNumber(
        Object.values(data.analysis).flat(),
      )
      if (totalDocNumber === docNumberToCheck) {
        setShowLink(false)
      }
    }
  }, [data])

  const [showMoreText, setShowMoreText] = useState(SHOW_MORE)
  const changeDisplayData = () => {
    if (showMoreEnabled) {
      setShowMoreText(SHOW_MORE)
    } else {
      setShowMoreText(SHOW_LESS)
    }
    setShowMoreEnabled(!showMoreEnabled)
  }

  const onClickConfirmOK = (analysisGUID: string) => {
    if (onClickConfirmOKForAnalysis) {
      onClickConfirmOKForAnalysis(analysisGUID)
    }
  }

  const onClickUndo = (analysisGUID: string, confirmationGUID: string) => {
    if (onClickUndoForAnalysis) {
      onClickUndoForAnalysis(analysisGUID, confirmationGUID)
    }
  }

  return (
    <Box width="100%" lineHeight="1.4">
      {displayData?.map(({ fields }) => {
        return (
          <Box>
            {fields.map((itemData) => {
              let {
                label,
                status,
                message,
                confirmation_details,
                id,
                doc_id,
                file_name,
              } = itemData
              const validConfirmationStatusRenter =
                confirmation_details?.filter(
                  (item) => item.confirmed_by === 'renter_confirmed',
                )[0]
              const validConfirmationStatusPM = confirmation_details?.filter(
                (item) => item.confirmed_by === 'pm_confirmed',
              )[0]
              if (
                (isRenterSide && validConfirmationStatusRenter) ||
                (!isRenterSide && validConfirmationStatusPM)
              ) {
                status = STATUS_CONFIRMED
              }
              if (!showMoreEnabled) {
                if (itemData.status === 'verified') {
                  return null
                }
              }
              return (
                <Box mb={5} lineHeight="1.4">
                  <HeadingAndLabel
                    heading={label}
                    status={status}
                    showOpenViewerLink={showOpenViewerLink}
                    toggleDocumentsViewer={
                      toggleDocumentsViewer
                        ? () => toggleDocumentsViewer(doc_id)
                        : () => {}
                    }
                    fileName={file_name}
                  />
                  <Box fontSize={theme.fontSizes.pRegular14} mb={3} mt={1}>
                    {message}
                  </Box>
                  {isRenterSide
                    ? !validConfirmationStatusRenter &&
                      !isCopilot && (
                        <Button
                          variant="outlineGray"
                          sizeVariant="extraSmall"
                          onClick={() => onClickConfirmOK(id)}
                        >
                          <Flex alignItems="center">
                            <Box>Confirm Ok</Box>
                          </Flex>
                        </Button>
                      )
                    : !validConfirmationStatusPM &&
                      !isCopilot && (
                        <Button
                          variant="outlineGray"
                          sizeVariant="extraSmall"
                          onClick={() => onClickConfirmOK(id)}
                        >
                          <Box>Confirm Ok</Box>
                        </Button>
                      )}
                  {isRenterSide
                    ? validConfirmationStatusRenter &&
                      !isCopilot && (
                        <Button
                          variant="outlineGray"
                          sizeVariant="extraSmall"
                          onClick={() =>
                            onClickUndo(id, validConfirmationStatusRenter?.id)
                          }
                        >
                          <Flex alignItems="center">
                            <UndoRounded /> <Box ml={3}>Undo</Box>
                          </Flex>
                        </Button>
                      )
                    : validConfirmationStatusPM &&
                      !isCopilot && (
                        <Button
                          variant="outlineGray"
                          sizeVariant="extraSmall"
                          onClick={() =>
                            onClickUndo(id, validConfirmationStatusPM?.id)
                          }
                        >
                          <Flex alignItems="center">
                            <UndoRounded /> <Box ml={3}>Undo</Box>
                          </Flex>
                        </Button>
                      )}
                </Box>
              )
            })}
          </Box>
        )
      })}
      {showLink && (
        <Button variant="linkBlueWithoutWeight" onClick={changeDisplayData}>
          {showMoreText}
        </Button>
      )}
    </Box>
  )
}

export default Explainer
